import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchStory, viewStory, fetchStories } from '../lib/api';
import { getDiscordAvatarUrl, formatTimeAgo } from '../lib/timeUtils';

const StoryViewer = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [currentStory, setCurrentStory] = useState(null);
  const [allStories, setAllStories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef(null);

  useEffect(() => {
    const loadStories = async () => {
      try {
        setLoading(true);
        const stories = await fetchStories();
        setAllStories(stories || []);
        
        const index = stories.findIndex(s => s.id === storyId);
        if (index !== -1) {
          setCurrentIndex(index);
          setCurrentStory(stories[index]);
        } else {
          const story = await fetchStory(storyId);
          setCurrentStory(story);
          setAllStories([story]);
          setCurrentIndex(0);
        }
      } catch (error) {
        console.error('Erro ao carregar stories:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStories();
  }, [storyId]);

  useEffect(() => {
    if (currentStory && !currentStory.is_viewed) {
      viewStory(currentStory.id).catch(err => console.error('Erro ao registrar visualização:', err));
    }
  }, [currentStory]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (currentIndex < allStories.length - 1) {
          const nextIndex = currentIndex + 1;
          setCurrentIndex(nextIndex);
          setCurrentStory(allStories[nextIndex]);
          navigate(`/story/${allStories[nextIndex].id}`, { replace: true });
        } else {
          navigate('/feed');
        }
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentIndex > 0) {
          const prevIndex = currentIndex - 1;
          setCurrentIndex(prevIndex);
          setCurrentStory(allStories[prevIndex]);
          navigate(`/story/${allStories[prevIndex].id}`, { replace: true });
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        navigate('/feed');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, allStories, navigate]);

  useEffect(() => {
    if (!currentStory) return;

    setProgress(0);
    const duration = 5000;
    const interval = 100;

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    let elapsed = 0;
    progressIntervalRef.current = setInterval(() => {
      elapsed += interval;
      const newProgress = (elapsed / duration) * 100;
      setProgress(newProgress);

      if (newProgress >= 100) {
        clearInterval(progressIntervalRef.current);
        handleNext();
      }
    }, interval);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [currentStory?.id, currentIndex, allStories.length]);

  const handleNext = () => {
    if (currentIndex < allStories.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      setCurrentStory(allStories[nextIndex]);
      navigate(`/story/${allStories[nextIndex].id}`, { replace: true });
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
      setCurrentStory(allStories[prevIndex]);
      navigate(`/story/${allStories[prevIndex].id}`, { replace: true });
    }
  };

  const handleClose = () => {
    navigate('/feed');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#1a1a1a] z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-white">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!currentStory) {
    return (
      <div className="fixed inset-0 bg-[#1a1a1a] z-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">Story não encontrada</p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-purple-600 rounded-md hover:bg-purple-700 transition-colors text-white"
          >
            Voltar ao Feed
          </button>
        </div>
      </div>
    );
  }

  const prevIndex = currentIndex > 0 ? currentIndex - 1 : allStories.length - 1;
  const nextIndex = currentIndex < allStories.length - 1 ? currentIndex + 1 : 0;
  const prevStory = allStories[prevIndex];
  const nextStory = allStories[nextIndex];

  const timeAgo = formatTimeAgo(currentStory.created_at);
  const prevTimeAgo = prevStory ? formatTimeAgo(prevStory.created_at) : '';
  const nextTimeAgo = nextStory ? formatTimeAgo(nextStory.created_at) : '';

  return (
    <div className="fixed inset-0 bg-[#1a1a1a] z-50 flex items-center justify-center overflow-hidden">
      {/* Background Blur Effect */}
      <div 
        className="absolute inset-0 opacity-30 blur-[80px] transition-all duration-500"
        style={{
          background: currentStory.image_url 
            ? `radial-gradient(circle at 50% 50%, #2a2a2a, #000)`
            : `radial-gradient(circle at 50% 50%, #2a2a2a, #000)`
        }}
      />

      {/* Top Controls */}
      <div className="absolute top-5 left-5 right-5 flex justify-between items-center z-[100]">
        <a 
          href="/feed" 
          onClick={(e) => { e.preventDefault(); handleClose(); }}
          className="font-extrabold text-2xl text-white no-underline flex items-center gap-2.5"
        >
          <span style={{ fontSize: '24px' }}>🌌</span> OASIS
        </a>
        <button 
          onClick={handleClose}
          className="bg-transparent border-0 text-white text-3xl cursor-pointer opacity-80 hover:opacity-100 transition-opacity"
        >
          <svg color="#ffffff" fill="#ffffff" height="24" role="img" viewBox="0 0 24 24" width="24">
            <polyline fill="none" points="20.643 3.357 12 12 3.353 20.647" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3"></polyline>
            <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" x1="20.649" x2="3.354" y1="20.649" y2="3.354"></line>
          </svg>
        </button>
      </div>

      {/* Stories Container */}
      <div className="relative z-10 w-full h-full flex items-center justify-center">
        
        {/* Previous Story Preview */}
        {prevStory && (
          <div 
            className="w-[200px] h-[350px] rounded-lg bg-[#333] opacity-40 scale-90 cursor-pointer relative overflow-hidden flex items-center justify-center transition-all hover:opacity-80 hover:scale-95"
            onClick={handlePrev}
          >
            <img 
              src={prevStory.image_url || ''} 
              alt="Prev" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-3 p-4">
              <div className="w-14 h-14 rounded-full border-2 border-purple-500 p-0.5">
                <img 
                  src={getDiscordAvatarUrl(prevStory.author_discord_id, prevStory.author_avatar)} 
                  alt="Avatar" 
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              <div className="font-semibold text-sm text-white text-center" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                {prevStory.author_username}
              </div>
              <div className="text-xs opacity-80 text-white">{prevTimeAgo}</div>
            </div>
          </div>
        )}

        {/* Arrow Left */}
        {currentIndex > 0 && (
          <button 
            className="absolute left-[calc(50%-225px)] top-1/2 -translate-y-1/2 w-[26px] h-[26px] bg-white/90 rounded-full border-0 cursor-pointer z-50 flex items-center justify-center shadow-[0_0_8px_rgba(0,0,0,0.2)] transition-all hover:bg-white hover:scale-110"
            onClick={handlePrev}
            style={{ color: '#1a1a1a' }}
          >
            <svg viewBox="0 0 24 24" style={{ width: '16px', height: '16px', fill: '#1a1a1a' }}>
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>
        )}

        {/* Main Active Story */}
        <div className="w-[380px] h-[85vh] max-h-[800px] bg-black rounded-xl relative overflow-hidden shadow-[0_0_20px_rgba(0,0,0,0.5)] flex flex-col z-10 mx-5">
          {/* Story Image */}
          {currentStory.image_url && (
            <img 
              src={currentStory.image_url} 
              alt="Story" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          {/* Story Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60 z-[2] flex flex-col justify-between p-5">
            {/* Top Section */}
            <div>
              {/* Progress Bar - Simples, uma barra por story */}
              <div className="w-full mb-3 flex gap-1">
                <div className="h-0.5 bg-white/30 rounded-sm flex-1 overflow-hidden">
                  <div 
                    className="h-full bg-white transition-all duration-100"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              
              {/* Header */}
              <div className="flex items-center gap-2.5">
                <img 
                  src={getDiscordAvatarUrl(currentStory.author_discord_id, currentStory.author_avatar)} 
                  alt="Avatar" 
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex gap-2 items-center">
                  <span className="font-semibold text-sm text-white">{currentStory.author_username}</span>
                  <span className="opacity-60 text-sm text-white">{timeAgo}</span>
                </div>
                <div className="ml-auto flex gap-4 text-xl cursor-pointer">
                  <svg aria-label="Mais opções" color="#ffffff" fill="#ffffff" height="24" role="img" viewBox="0 0 24 24" width="24">
                    <circle cx="12" cy="12" r="1.5"></circle>
                    <circle cx="6" cy="12" r="1.5"></circle>
                    <circle cx="18" cy="12" r="1.5"></circle>
                  </svg>
                </div>
              </div>
            </div>

            {/* Story Content */}
            {!currentStory.image_url && currentStory.text_content && (
              <div className="flex-1 flex items-center justify-center p-8">
                <p className="text-white text-2xl font-semibold text-center leading-relaxed">
                  {currentStory.text_content}
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center gap-4 mb-2.5">
              <input 
                type="text" 
                className="flex-1 bg-transparent border border-white/50 rounded-[25px] px-5 py-3.5 text-white text-sm outline-none transition-colors placeholder:text-white focus:border-white"
                placeholder={`Responder a ${currentStory.author_username}...`}
              />
              <button className="bg-transparent border-0 text-white text-2xl cursor-pointer transition-transform hover:scale-110">
                <svg aria-label="Curtir" color="#ffffff" fill="#ffffff" height="24" role="img" viewBox="0 0 24 24" width="24">
                  <path d="M16.792 3.904A4.989 4.989 0 0 1 21.5 9.122c0 3.072-2.652 4.959-5.197 7.222-2.512 2.243-3.865 3.469-4.303 3.752-.477-.309-2.143-1.823-4.303-3.752C5.141 14.072 2.5 12.167 2.5 9.122a4.989 4.989 0 0 1 4.708-5.218 4.21 4.21 0 0 1 3.675 1.941c.84 1.175.98 1.763 1.12 1.763s.278-.588 1.11-1.766a4.17 4.17 0 0 1 3.679-1.938m0-2a6.04 6.04 0 0 0-4.797 2.127 6.052 6.052 0 0 0-4.787-2.127A6.985 6.985 0 0 0 .5 9.122c0 3.61 2.55 5.827 5.015 7.97.283.246.569.494.853.747l1.027.918a44.998 44.998 0 0 0 3.518 3.018 2 2 0 0 0 2.174 0 45.263 45.263 0 0 0 3.626-3.115l.922-.824c.293-.26.59-.519.885-.774 2.334-2.025 4.98-4.32 4.98-7.94a6.985 6.985 0 0 0-6.708-7.218Z"></path>
                </svg>
              </button>
              <button className="bg-transparent border-0 text-white text-2xl cursor-pointer -rotate-[30deg] -mt-1">
                <svg aria-label="Compartilhar" color="#ffffff" fill="#ffffff" height="24" role="img" viewBox="0 0 24 24" width="24">
                  <line fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" x1="22" x2="9.218" y1="3" y2="10.083"></line>
                  <polygon fill="none" points="11.698 20.334 22 3.001 2 3.001 9.218 10.084 11.698 20.334" stroke="currentColor" strokeLinejoin="round" strokeWidth="2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Arrow Right */}
        {currentIndex < allStories.length - 1 && (
          <button 
            className="absolute right-[calc(50%-225px)] top-1/2 -translate-y-1/2 w-[26px] h-[26px] bg-white/90 rounded-full border-0 cursor-pointer z-50 flex items-center justify-center shadow-[0_0_8px_rgba(0,0,0,0.2)] transition-all hover:bg-white hover:scale-110"
            onClick={handleNext}
            style={{ color: '#1a1a1a' }}
          >
            <svg viewBox="0 0 24 24" style={{ width: '16px', height: '16px', fill: '#1a1a1a' }}>
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </button>
        )}

        {/* Next Story Preview */}
        {nextStory && (
          <div 
            className="w-[200px] h-[350px] rounded-lg bg-[#333] opacity-40 scale-90 cursor-pointer relative overflow-hidden flex items-center justify-center transition-all hover:opacity-80 hover:scale-95"
            onClick={handleNext}
          >
            <img 
              src={nextStory.image_url || ''} 
              alt="Next" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-3 p-4">
              <div className="w-14 h-14 rounded-full border-2 border-purple-500 p-0.5">
                <img 
                  src={getDiscordAvatarUrl(nextStory.author_discord_id, nextStory.author_avatar)} 
                  alt="Avatar" 
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              <div className="font-semibold text-sm text-white text-center" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                {nextStory.author_username}
              </div>
              <div className="text-xs opacity-80 text-white">{nextTimeAgo}</div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default StoryViewer;
